import { useAuth0 } from '@auth0/auth0-react'
import {
  Button,
  CircularProgress,
  Container,
  createStyles,
  Grow,
  makeStyles,
  Paper,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core'
import { useController } from 'customHooks/useController'
import { isEqual } from 'lodash'
import queryString from 'query-string'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthService } from 'services/auth'
import { useAppSelector } from 'store/configureStore'
import { AcceptInviteController } from './controllers/AcceptInviteController'
import { InvitationFlowStateMachine } from './InvitationFlowStateMachine'
import { useMachine } from '@xstate/react'
import { UserController } from './controllers/UserController'
import { AppLogo } from 'components/Layout/AppLogo'
import { CheckOutlined, ErrorOutlineOutlined } from '@material-ui/icons'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    acceptInviteRoot: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      overflow: 'auto',
    },
    container: {
      alignItems: 'center',
    },
    header: {
      padding: `${theme.spacing(4)}px`,
      display: 'flex',
      justifyContent: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& img': {
        maxWidth: '100%',
      },
    },
    content: {
      padding: `${theme.spacing(2)}px`,
      display: 'flex',
      flexDirection: 'column',
      gap: `${theme.spacing(4)}px`,
      textAlign: 'center',
    },
    progress: {
      padding: theme.spacing(0, 5),
      '& svg': {
        width: '64px',
        height: '64px',
      },
    },
  })
)

const { id, code } = queryString.parse(window.location.search)

export const AcceptInvitePage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const classes = useStyles()
  const auth0 = useAuth0()
  const theme = useTheme()

  const [state, send] = useMachine(InvitationFlowStateMachine, {
    context: {
      invitationId: location.state?.invitationId ?? (id as string) ?? '',
      invitationCode: location.state?.invitationCode ?? (code as string) ?? '',
    },
  })

  const { controller } = useController(() => new AcceptInviteController())
  const { controller: userController } = useController(
    () => new UserController()
  )
  useEffect(() => {
    send('CHANGE_DATA', { data: { inviteController: controller } })
  }, [controller, send])

  const rh24User = useAppSelector((state) => state.user, isEqual)

  useEffect(() => {
    send('CHANGE_DATA', { data: { user: rh24User } })
  }, [rh24User, send])

  const tenantId = useAppSelector((state) => state.user.tenantId)
  useEffect(() => {
    send('CHANGE_DATA', { data: { tenantId } })
  }, [send, tenantId])

  useEffect(() => {
    if (auth0.isAuthenticated) {
      AuthService.Build(auth0)
    }
    send('CHANGE_DATA', { data: { auth0 } })
  }, [auth0, send])

  useEffect(() => {
    send('CHANGE_DATA', { data: { theme } })
  }, [send, theme])

  useEffect(() => {
    send('CHANGE_DATA', { data: { navigate } })
  }, [navigate, send])

  useEffect(() => {
    send('CHANGE_DATA', { data: { userController } })
  }, [send, userController])

  return (
    <div className={classes.acceptInviteRoot}>
      <Container maxWidth="xs" className={classes.container}>
        <Grow in={true}>
          <Paper>
            <div className={classes.header}>
              <AppLogo />
            </div>
            <div className={classes.content}>
              <LocalizedTypography
                translationKey="invite:page-title"
                variant="h5"
              >
                setting up your environment
              </LocalizedTypography>
              <div className={classes.progress}>
                <ProgressIcon state={state} />
              </div>
              <div>
                <LocalizedTypography
                  translationKey={`invite:state-${state.value}`}
                  defaultValue={state.value.toString()}
                />
              </div>
            </div>
            {auth0.isAuthenticated && (
              <Button
                fullWidth
                onClick={() =>
                  auth0.logout({ returnTo: window.location.origin })
                }
              >
                logout
              </Button>
            )}
          </Paper>
        </Grow>
      </Container>
    </div>
  )
}

const ProgressIcon = (props: { state }) => {
  if (props.state.context.errors?.length) {
    return <ShowErrors errors={props.state.context.errors} />
  }
  if (props.state.matches('completed')) {
    return <CheckOutlined color="primary" />
  }
  if (props.state.matches('error')) {
    return <ErrorOutlineOutlined color="secondary" />
  }

  return <CircularProgress size="64px" />
}

const ShowErrors = (props: {
  errors: Array<{ message: string; status: number }>
}) => {
  return (
    <>
      <ErrorOutlineOutlined color="secondary" />
      <ul>
        {props.errors.map((x, i) => (
          <Typography key={i} component="li">
            {x.message}
          </Typography>
        ))}
      </ul>
    </>
  )
}
